import { Observable } from 'rxjs';

export abstract class ViewModelBase {
  private originalValues?: Map<string, any>;

  get hasChanges(): boolean {
    return (this.originalValues?.size ?? 0) > 0;
  }

  constructor(private item?: any) {}

  protected super(item: any) {
    if (item) {
      this.item = item;
    }
  }

  protected isChanged(property: string): boolean {
    return this.originalValues !== undefined && this.originalValues.has(property);
  }

  protected getOriginalValue(property: string): any {
    return this.isChanged(property) ? this.originalValues!.get(property) : this.getValueCore(this.item, property);
  }

  protected setValue(key: string, oldValue: any, newValue: any, item?: any): boolean {
    if (oldValue === newValue) {
      return false;
    }

    this.setValueCore(item ?? this.item, key, newValue);

    if (!this.originalValues) {
      this.originalValues = new Map<string, any>();
    }

    if (!this.originalValues.has(key)) {
      this.originalValues.set(key, oldValue);
    } else if (this.originalValues.get(key) === newValue) {
      this.originalValues.delete(key);
    }

    return true;
  }

  protected setValueCore(item: any, key: string, value: any): void {
    if (key.indexOf('.') >= 0) {
      const keys = key.split('.');

      for (let i = 0; i < keys.length - 1; i++) {
        item = item[keys[i]];
      }

      item[keys[keys.length - 1]] = value;
    } else {
      item[key] = value;
    }
  }

  private getValueCore(item: any, key: string): any {
    if (key.indexOf('.') >= 0) {
      const keys = key.split('.');

      for (let i = 0; i < keys.length - 1; i++) {
        item = item[keys[i]];
      }

      return item[keys[keys.length - 1]];
    } else {
      return item[key];
    }
  }

  reset(): void {
    if (this.originalValues) {
      this.originalValues.forEach((value, key) => {
        this.setValueCore(this.item, key, value);
      });
      this.originalValues = undefined;
    }
  }

  accept(): void {
    this.originalValues = undefined;
  }
}

export class ClickableData {
  id: string;
  objectType: number;
  hasPhoto: boolean;
  hasVideo: boolean;
  left: number;
  right: number;
  top: number;
  bottom: number;
  points?: [{ x: number; y: number }];

  constructor(data?: any) {
    Object.assign(this, data);
  }
}

export class PipeData {
  bezeichnung: string = '';
  strasse: string = '';

  constructor(data?: any) {
    Object.assign(this, data);
  }
}

export class NodeData {
  bezeichnung: string = '';
  strasse: string = '';

  constructor(data?: any) {
    Object.assign(this, data);
  }
}

export class PipeGraphicsTable {
  id: string = '';
  isPipe = false;
  isInspection = false;
  isRehabilitation = false;
  columns: { name: string; caption: string; format?: string; width?: string; minWidth?: number; isBanded?: boolean }[] =
    [];
  rows = [];

  constructor(data?: any) {
    Object.assign(this, data);
  }
}

export class NodeGraphicsTable {
  id: string = '';
  isPipe = false;
  isInspection = false;
  isRehabilitation = false;
  columns: { name: string; caption: string; format?: string; width?: string; minWidth?: number; isBanded?: boolean }[] =
    [];
  rows = [];

  constructor(data?: any) {
    Object.assign(this, data);
  }
}

export class PipeGraphicsState {
  isActive: boolean = false;
  isActiveAdditionalId: boolean = false;
  view: string = '';
  additionalId: string = '';
  configurationGroup: string = '';
  configurationGroupEntry: string = '';
  hideStatusTextsIfNotRelevant: boolean = false;

  inspectionId: string = '';
  rehabilitationId: string = '';
  reportFilename: string = '';
  additionalIds: { id: string; displayText: string }[] = [];

  imageBase64: string;
  imageWidth: number;
  imageHeight: number;
  clickableData: ClickableData[];

  table: PipeGraphicsTable;
}

export class NodeGraphicsState {
  isActive: boolean = false;
  isActiveAdditionalId: boolean = false;
  view: string = '';
  additionalId: string = '';
  configurationGroup: string = '';
  configurationGroupEntry: string = '';
  showAllPipes: boolean = false;
  hideStatusTextsIfNotRelevant: boolean = false;
  dimensioningName: string = '';

  dimensioningSpacing: number | null = null;
  pipeDefaultLength: number | null = null;
  inspectionId: string = '';
  rehabilitationId: string = '';
  reportFilename: string = '';
  additionalIds: { id: string; displayText: string }[] = [];

  imageBase64: string;
  imageWidth: number;
  imageHeight: number;
  clickableData: ClickableData[];

  table: NodeGraphicsTable;
}

export class PipeGraphicsSettings {
  graphicsDocumentsSocketBranch: { id: string; displayText: string }[] = [];
  graphicsDocumentIdSocketBranch: string = '';
  graphicsDocumentsInspection: { id: string; displayText: string }[] = [];
  graphicsDocumentIdInspection: string = '';
  configurationGroupsInspection: { id: string; displayText: string }[] = [];
  configurationGroupInspection: string = '';
  configurationGroupEntriesInspection: {
    configurationGroup: string;
    id: string;
    displayText: string;
    isActive: boolean;
  }[] = [];
  configurationGroupEntryInspection: string = '';
  reportDocuments: { id: string; displayText: string; type: string }[] = [];
  reportDocumentIdSocketBranch: string = '';
  reportDocumentIdSingleInspection: string = '';
  reportDocumentIdDoubleInspection: string = '';
  reportDocumentIdInspectionRehabilitation: string = '';
  reportDocumentIdSingleRehabilitation: string = '';
  showTable: boolean = false;
  showMedia: boolean = false;
  hideStatusTextsIfNotRelevant: boolean = false;
  showPhotosInReport: boolean = false;
  hideHeaderInReport: boolean = false;

  constructor(data?: any) {
    Object.assign(this, data);
  }
}

export class NodeGraphicsSettings {
  graphicsDocumentsOutline: { id: string; displayText: string }[] = [];
  graphicsDocumentIdOutline: string = '';
  graphicsDocumentsInspectionWide: { id: string; displayText: string }[] = [];
  graphicsDocumentIdInspectionWide: string = '';
  graphicsDocumentsInspectionLong: { id: string; displayText: string }[] = [];
  graphicsDocumentIdInspectionLong: string = '';
  configurationGroupsOutline: { id: string; displayText: string }[] = [];
  configurationGroupOutline: string = '';
  configurationGroupsInspectionLong: { id: string; displayText: string }[] = [];
  configurationGroupInspectionLong: string = '';
  configurationGroupEntriesInspectionWide: { id: string; displayText: string }[] = [];
  configurationGroupEntryInspectionWide: string = '';
  configurationGroupEntriesInspectionLong: {
    configurationGroup: string;
    id: string;
    displayText: string;
    isActive: boolean;
  }[] = [];
  configurationGroupEntryInspectionLong: string = '';
  reportDocuments: { id: string; displayText: string; type: string }[] = [];
  reportDocumentIdInspection: string = '';
  reportDocumentIdOutline: string = '';
  showTable: boolean = false;
  showMedia: boolean = false;
  showAllPipes: boolean = false;
  hideStatusTextsIfNotRelevant: boolean = false;
  showPhotosInReport: boolean = false;
  pipeDefaultLength: number | null = null;
  dimensioningSpacing: number | null = null;
  dimensioningNames: { id: string; displayText: string }[] = [];
  dimensioningName: string = '';

  constructor(data?: any) {
    Object.assign(this, data);
  }
}

export class PipeGraphicsSettingsViewModel extends ViewModelBase {
  readonly reportDocumentsSocketBranch: { id: string; displayText: string; type: string }[] = [];
  readonly reportDocumentsSingleInspection: { id: string; displayText: string; type: string }[] = [];
  readonly reportDocumentsDoubleInspection: { id: string; displayText: string; type: string }[] = [];
  readonly reportDocumentsInspectionRehabilitation: { id: string; displayText: string; type: string }[] = [];
  readonly reportDocumentsSingleRehabilitation: { id: string; displayText: string; type: string }[] = [];

  get graphicsDocumentIdSocketBranch(): string {
    return this.pipeGraphicsSettings.graphicsDocumentIdSocketBranch ?? '';
  }
  set graphicsDocumentIdSocketBranch(value: string) {
    this.setValue('graphicsDocumentIdSocketBranch', this.graphicsDocumentIdSocketBranch, value ?? '');
  }
  get graphicsDocumentIdSocketBranchHasChanged(): boolean {
    return this.isChanged('graphicsDocumentIdSocketBranch');
  }

  get graphicsDocumentIdInspection(): string {
    return this.pipeGraphicsSettings.graphicsDocumentIdInspection ?? '';
  }
  set graphicsDocumentIdInspection(value: string) {
    this.setValue('graphicsDocumentIdInspection', this.graphicsDocumentIdInspection, value ?? '');
  }
  get graphicsDocumentIdInspectionHasChanged(): boolean {
    return this.isChanged('graphicsDocumentIdInspection');
  }

  get configurationGroupEntryInspection(): string {
    return this.pipeGraphicsSettings.configurationGroupEntryInspection ?? '';
  }
  set configurationGroupEntryInspection(value: string) {
    this.setValue('configurationGroupEntryInspection', this.configurationGroupEntryInspection, value ?? '');
  }
  get configurationGroupEntryInspectionHasChanged(): boolean {
    return this.isChanged('configurationGroupEntryInspection');
  }

  get reportDocumentIdSocketBranch(): string {
    return this.pipeGraphicsSettings.reportDocumentIdSocketBranch ?? '';
  }
  set reportDocumentIdSocketBranch(value: string) {
    this.setValue('reportDocumentIdSocketBranch', this.reportDocumentIdSocketBranch, value ?? '');
  }
  get reportDocumentIdSocketBranchHasChanged(): boolean {
    return this.isChanged('reportDocumentIdSocketBranch');
  }

  get reportDocumentIdSingleInspection(): string {
    return this.pipeGraphicsSettings.reportDocumentIdSingleInspection ?? '';
  }
  set reportDocumentIdSingleInspection(value: string) {
    this.setValue('reportDocumentIdSingleInspection', this.reportDocumentIdSingleInspection, value ?? '');
  }
  get reportDocumentIdSingleInspectionHasChanged(): boolean {
    return this.isChanged('reportDocumentIdSingleInspection');
  }

  get reportDocumentIdDoubleInspection(): string {
    return this.pipeGraphicsSettings.reportDocumentIdDoubleInspection ?? '';
  }
  set reportDocumentIdDoubleInspection(value: string) {
    this.setValue('reportDocumentIdDoubleInspection', this.reportDocumentIdDoubleInspection, value ?? '');
  }
  get reportDocumentIdDoubleInspectionHasChanged(): boolean {
    return this.isChanged('reportDocumentIdDoubleInspection');
  }

  get reportDocumentIdInspectionRehabilitation(): string {
    return this.pipeGraphicsSettings.reportDocumentIdInspectionRehabilitation ?? '';
  }
  set reportDocumentIdInspectionRehabilitation(value: string) {
    this.setValue(
      'reportDocumentIdInspectionRehabilitation',
      this.reportDocumentIdInspectionRehabilitation,
      value ?? '',
    );
  }
  get reportDocumentIdInspectionRehabilitationHasChanged(): boolean {
    return this.isChanged('reportDocumentIdInspectionRehabilitation');
  }

  get reportDocumentIdSingleRehabilitation(): string {
    return this.pipeGraphicsSettings.reportDocumentIdSingleRehabilitation ?? '';
  }
  set reportDocumentIdSingleRehabilitation(value: string) {
    this.setValue('reportDocumentIdSingleRehabilitation', this.reportDocumentIdSingleRehabilitation, value ?? '');
  }
  get reportDocumentIdSingleRehabilitationHasChanged(): boolean {
    return this.isChanged('reportDocumentIdSingleRehabilitation');
  }

  get showPhotosInReport(): boolean {
    return this.pipeGraphicsSettings.showPhotosInReport ?? false;
  }
  set showPhotosInReport(value: boolean) {
    this.setValue('showPhotosInReport', this.showPhotosInReport, value ?? false);
  }
  get showPhotosInReportHasChanged(): boolean {
    return this.isChanged('showPhotosInReport');
  }

  get hideHeaderInReport(): boolean {
    return this.pipeGraphicsSettings.hideHeaderInReport ?? false;
  }
  set hideHeaderInReport(value: boolean) {
    this.setValue('hideHeaderInReport', this.hideHeaderInReport, value ?? false);
  }
  get hideHeaderInReportHasChanged(): boolean {
    return this.isChanged('hideHeaderInReport');
  }

  get hideStatusTextsIfNotRelevant(): boolean {
    return this.pipeGraphicsSettings.hideStatusTextsIfNotRelevant ?? false;
  }
  set hideStatusTextsIfNotRelevant(value: boolean) {
    this.setValue('hideStatusTextsIfNotRelevant', this.hideStatusTextsIfNotRelevant, value ?? false);
  }
  get hideStatusTextsIfNotRelevantHasChanged(): boolean {
    return this.isChanged('hideStatusTextsIfNotRelevant');
  }

  configurationGroupEntriesInspection: { id: string; displayText: string; isActive: boolean }[] = [];

  constructor(readonly pipeGraphicsSettings: PipeGraphicsSettings) {
    super(pipeGraphicsSettings);

    this.reportDocumentsSocketBranch = this.filterReportDocuments(
      StringLiteral.ReportDocumentTypeSocketBranch,
      pipeGraphicsSettings.reportDocumentIdSocketBranch,
    );
    this.reportDocumentsSingleInspection = this.filterReportDocuments(
      StringLiteral.ReportDocumentTypeSingleInspection,
      pipeGraphicsSettings.reportDocumentIdSingleInspection,
    );
    this.reportDocumentsDoubleInspection = this.filterReportDocuments(
      StringLiteral.ReportDocumentTypeDoubleInspection,
      pipeGraphicsSettings.reportDocumentIdDoubleInspection,
    );
    this.reportDocumentsInspectionRehabilitation = this.filterReportDocuments(
      StringLiteral.ReportDocumentTypeInspectionRehabilitation,
      pipeGraphicsSettings.reportDocumentIdInspectionRehabilitation,
    );
    this.reportDocumentsSingleRehabilitation = this.filterReportDocuments(
      StringLiteral.ReportDocumentTypeSingleRehabilitation,
      pipeGraphicsSettings.reportDocumentIdSingleRehabilitation,
    );
  }

  private filterReportDocuments(
    reportDocumentType: string,
    currentReportDocumentId: string,
  ): { id: string; displayText: string; type: string }[] {
    return this.pipeGraphicsSettings.reportDocuments.filter(
      (reportDocument) =>
        !reportDocument.type ||
        reportDocument.type === reportDocumentType ||
        reportDocument.id === currentReportDocumentId,
    );
  }

  refreshConfigurationGroupEntriesInspection(configurationGroupInspection: string): void {
    const configurationGroupEntriesInspection = this.pipeGraphicsSettings.configurationGroupEntriesInspection.filter(
      (configurationGroupEntry) => configurationGroupEntry.configurationGroup === configurationGroupInspection,
    );
    this.configurationGroupEntriesInspection = configurationGroupEntriesInspection;
    this.configurationGroupEntryInspection = (
      configurationGroupEntriesInspection.find((configurationGroupEntry) => configurationGroupEntry.isActive) ??
      configurationGroupEntriesInspection[0]
    ).id;
    this.accept();
  }
}

export class NodeGraphicsSettingsViewModel extends ViewModelBase {
  readonly reportDocumentsOutline: { id: string; displayText: string; type: string }[] = [];
  readonly reportDocumentsInspection: { id: string; displayText: string; type: string }[] = [];

  get graphicsDocumentIdOutline(): string {
    return this.nodeGraphicsSettings.graphicsDocumentIdOutline ?? '';
  }
  set graphicsDocumentIdOutline(value: string) {
    this.setValue('graphicsDocumentIdOutline', this.graphicsDocumentIdOutline, value ?? '');
  }
  get graphicsDocumentIdOutlineHasChanged(): boolean {
    return this.isChanged('graphicsDocumentIdOutline');
  }

  get graphicsDocumentIdInspectionWide(): string {
    return this.nodeGraphicsSettings.graphicsDocumentIdInspectionWide ?? '';
  }
  set graphicsDocumentIdInspectionWide(value: string) {
    this.setValue('graphicsDocumentIdInspectionWide', this.graphicsDocumentIdInspectionWide, value ?? '');
  }
  get graphicsDocumentIdInspectionWideHasChanged(): boolean {
    return this.isChanged('graphicsDocumentIdInspectionWide');
  }

  get graphicsDocumentIdInspectionLong(): string {
    return this.nodeGraphicsSettings.graphicsDocumentIdInspectionLong ?? '';
  }
  set graphicsDocumentIdInspectionLong(value: string) {
    this.setValue('graphicsDocumentIdInspectionLong', this.graphicsDocumentIdInspectionLong, value ?? '');
  }
  get graphicsDocumentIdInspectionLongHasChanged(): boolean {
    return this.isChanged('graphicsDocumentIdInspectionLong');
  }

  get configurationGroupEntryInspectionWide(): string {
    return this.nodeGraphicsSettings.configurationGroupEntryInspectionWide ?? '';
  }
  set configurationGroupEntryInspectionWide(value: string) {
    this.setValue('configurationGroupEntryInspectionWide', this.configurationGroupEntryInspectionWide, value ?? '');
  }
  get configurationGroupEntryInspectionWideHasChanged(): boolean {
    return this.isChanged('configurationGroupEntryInspectionWide');
  }

  get configurationGroupEntryInspectionLong(): string {
    return this.nodeGraphicsSettings.configurationGroupEntryInspectionLong ?? '';
  }
  set configurationGroupEntryInspectionLong(value: string) {
    this.setValue('configurationGroupEntryInspectionLong', this.configurationGroupEntryInspectionLong, value ?? '');
  }
  get configurationGroupEntryInspectionLongHasChanged(): boolean {
    return this.isChanged('configurationGroupEntryInspectionLong');
  }

  get reportDocumentIdOutline(): string {
    return this.nodeGraphicsSettings.reportDocumentIdOutline ?? '';
  }
  set reportDocumentIdOutline(value: string) {
    this.setValue('reportDocumentIdOutline', this.reportDocumentIdOutline, value ?? '');
  }
  get reportDocumentIdOutlineHasChanged(): boolean {
    return this.isChanged('reportDocumentIdOutline');
  }

  get reportDocumentIdInspection(): string {
    return this.nodeGraphicsSettings.reportDocumentIdInspection ?? '';
  }
  set reportDocumentIdInspection(value: string) {
    this.setValue('reportDocumentIdInspection', this.reportDocumentIdInspection, value ?? '');
  }
  get reportDocumentIdInspectionHasChanged(): boolean {
    return this.isChanged('reportDocumentIdInspection');
  }

  get showPhotosInReport(): boolean {
    return this.nodeGraphicsSettings.showPhotosInReport ?? false;
  }
  set showPhotosInReport(value: boolean) {
    this.setValue('showPhotosInReport', this.showPhotosInReport, value ?? false);
  }
  get showPhotosInReportHasChanged(): boolean {
    return this.isChanged('showPhotosInReport');
  }

  get hideStatusTextsIfNotRelevant(): boolean {
    return this.nodeGraphicsSettings.hideStatusTextsIfNotRelevant ?? false;
  }
  set hideStatusTextsIfNotRelevant(value: boolean) {
    this.setValue('hideStatusTextsIfNotRelevant', this.hideStatusTextsIfNotRelevant, value ?? false);
  }
  get hideStatusTextsIfNotRelevantHasChanged(): boolean {
    return this.isChanged('hideStatusTextsIfNotRelevant');
  }

  get showAllPipes(): boolean {
    return this.nodeGraphicsSettings.showAllPipes ?? false;
  }
  set showAllPipes(value: boolean) {
    this.setValue('showAllPipes', this.showAllPipes, value ?? false);
  }
  get showAllPipesHasChanged(): boolean {
    return this.isChanged('showAllPipes');
  }

  get pipeDefaultLength(): number | null {
    return this.nodeGraphicsSettings.pipeDefaultLength ?? null;
  }
  set pipeDefaultLength(value: number | null) {
    this.setValue('pipeDefaultLength', this.pipeDefaultLength, value ?? null);
  }
  get pipeDefaultLengthHasChanged(): boolean {
    return this.isChanged('pipeDefaultLength');
  }

  get dimensioningSpacing(): number | null {
    return this.nodeGraphicsSettings.dimensioningSpacing ?? null;
  }
  set dimensioningSpacing(value: number | null) {
    this.setValue('dimensioningSpacing', this.dimensioningSpacing, value ?? null);
  }
  get dimensioningSpacingHasChanged(): boolean {
    return this.isChanged('dimensioningSpacing');
  }

  get dimensioningName(): string {
    return this.nodeGraphicsSettings.dimensioningName ?? '';
  }
  set dimensioningName(value: string) {
    this.setValue('dimensioningName', this.dimensioningName, value ?? '');
  }
  get dimensioningNameHasChanged(): boolean {
    return this.isChanged('dimensioningName');
  }

  configurationGroupEntriesInspectionLong: { id: string; displayText: string; isActive: boolean }[] = [];

  constructor(readonly nodeGraphicsSettings: NodeGraphicsSettings) {
    super(nodeGraphicsSettings);

    this.reportDocumentsOutline = this.filterReportDocuments(
      StringLiteral.ReportDocumentTypeOutline,
      nodeGraphicsSettings.reportDocumentIdOutline,
    );
    this.reportDocumentsInspection = this.filterReportDocuments(
      StringLiteral.ReportDocumentTypeInspection,
      nodeGraphicsSettings.reportDocumentIdInspection,
    );
  }

  private filterReportDocuments(
    reportDocumentType: string,
    currentReportDocumentId: string,
  ): { id: string; displayText: string; type: string }[] {
    return this.nodeGraphicsSettings.reportDocuments.filter(
      (reportDocument) =>
        !reportDocument.type ||
        reportDocument.type === reportDocumentType ||
        reportDocument.id === currentReportDocumentId,
    );
  }

  refreshConfigurationGroupEntriesInspectionLong(configurationGroupInspectionLong: string): void {
    const configurationGroupEntriesInspectionLong =
      this.nodeGraphicsSettings.configurationGroupEntriesInspectionLong.filter(
        (configurationGroupEntry) => configurationGroupEntry.configurationGroup === configurationGroupInspectionLong,
      );
    this.configurationGroupEntriesInspectionLong = configurationGroupEntriesInspectionLong;
    this.configurationGroupEntryInspectionLong = (
      configurationGroupEntriesInspectionLong.find((configurationGroupEntry) => configurationGroupEntry.isActive) ??
      configurationGroupEntriesInspectionLong[0]
    ).id;
    this.accept();
  }
}

export enum StringLiteral {
  ViewSocketBranch = 'SocketBranch',
  ViewInspection = 'Inspection',
  ViewOutline = 'Outline',
  ViewInspectionWide = 'InspectionWide',
  ViewInspectionLong = 'InspectionLong',
  ReportDocumentTypeSocketBranch = 'SocketBranch',
  ReportDocumentTypeSingleInspection = 'SingleInspection',
  ReportDocumentTypeDoubleInspection = 'DoubleInspection',
  ReportDocumentTypeInspectionRehabilitation = 'InspectionRehabilitation',
  ReportDocumentTypeSingleRehabilitation = 'SingleRehabilitation',
  ReportDocumentTypeOutline = 'Outline',
  ReportDocumentTypeInspection = 'Inspection',
}
